
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ExpertApplication } from "@/types/admin";

export function useExpertApplications() {
  const [applications, setApplications] = useState<ExpertApplication[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchApplications = async (forceRefresh = false) => {
    setLoading(true);
    try {
      console.log("🔄 Fetching expert applications...", forceRefresh ? "(forced refresh)" : "");
      
      // Add a longer delay for force refresh to ensure database consistency
      if (forceRefresh) {
        console.log("⏳ Force refresh - waiting for database consistency...");
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // Use a more explicit query with error handling
      const { data, error, count } = await supabase
        .from("expert_applications")
        .select("*", { count: 'exact' })
        .order("created_at", { ascending: false });

      if (error) {
        console.error("❌ Error fetching applications:", error);
        throw error;
      }

      console.log("✅ Fetched applications:", data?.length || 0);
      console.log("📊 Database returned count:", count);
      console.log("📊 Raw applications data:", data);
      
      // Filter out any 'under_review' status applications and normalize the data
      const normalizedData = data?.filter(app => app.status !== 'under_review').map(app => ({
        ...app,
        status: app.status as 'pending' | 'approved' | 'rejected',
        package_pricing: app.package_pricing as any
      })) || [];
      
      // Log the status distribution with detailed breakdown
      const statusCounts = normalizedData.reduce((acc, app) => {
        acc[app.status] = (acc[app.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      console.log("📊 Status distribution after fetch:", statusCounts);
      
      // Log each application's current status for debugging
      normalizedData.forEach(app => {
        console.log(`📋 App ${app.id.substring(0, 8)}: ${app.full_name} - Status: ${app.status} - Updated: ${app.updated_at}`);
      });
      
      setApplications(normalizedData);
    } catch (error) {
      console.error("💥 Error in fetchApplications:", error);
      toast.error("Failed to load expert applications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  return {
    applications,
    loading,
    fetchApplications: (forceRefresh = false) => fetchApplications(forceRefresh)
  };
}
